import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Plus, Pencil, Trash2, ImagePlus, Store, Package, Layers,
  Settings2, ExternalLink, Upload, Eye, EyeOff, Download,
} from "lucide-react";
import { api, fileUrl, formatApiError, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { InstagramImport } from "@/components/InstagramImport";
import { AdminFeedback } from "@/components/AdminFeedback";
import { AdminAccounts } from "@/components/AdminAccounts";

const EMPTY_FORM = { name: "", description: "", price: "", mrp: "", category: "Sarees", tag: "", image_url: "", images: [], in_stock: true };
const CATEGORIES = ["Sarees", "Kurtis"];

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({ whatsapp_number: "", instagram_url: "", offer_text: "" });
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => toast.error("Products load nahi hue"));
  const loadSettings = () => api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});

  const exportTaps = async () => {
    try {
      const r = await api.get("/admin/export/taps", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "ira-lakshmi-tap-report.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (err) {
      toast.error(formatApiError(err, "Could not download the report"));
    }
  };

  useEffect(() => {
    const onExpired = () => { setUser(null); toast.error("Session expired — please log in again."); };
    window.addEventListener("auth:expired", onExpired);
    api.get("/auth/me").then((r) => { setUser(r.data); loadProducts(); loadSettings(); }).catch(() => setUser(null));
    return () => window.removeEventListener("auth:expired", onExpired);
  }, []);

  const stats = useMemo(() => ({
    total: products.length,
    categories: new Set(products.map((p) => p.category)).size,
    inStock: products.filter((p) => p.in_stock).length,
  }), [products]);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/admin/upload", fd);
        urls.push(r.data.url);
      }
      setForm((s) => ({ ...s, images: [...(s.images || []), ...urls] }));
      toast.success(`${urls.length} photo(s) uploaded`);
    } catch (err) {
      toast.error(formatApiError(err, "Image upload failed"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (i) => setForm((s) => ({ ...s, images: s.images.filter((_, x) => x !== i) }));
  const makeCover = (i) => setForm((s) => ({ ...s, images: [s.images[i], ...s.images.filter((_, x) => x !== i)] }));

  const saveProduct = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.price) return toast.error("Name and price are required");
    if (!form.images || !form.images.length) return toast.error("Add at least one product photo");
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price), name: form.name.trim(), mrp: form.mrp ? Number(form.mrp) : null, image_url: form.images[0] || "", images: form.images };
      if (editingId) {
        await api.put(`/admin/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product published — it's live on your shop!");
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      loadProducts();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const editProduct = (p) => {
    setEditingId(p.id);
    setForm({ name: p.name, description: p.description || "", price: String(p.price), mrp: p.mrp != null ? String(p.mrp) : "", category: p.category || "Sarees", tag: p.tag || "", image_url: p.image_url || "", images: (p.images && p.images.length ? p.images : (p.image_url ? [p.image_url] : [])), in_stock: p.in_stock !== false });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleStock = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}`, { name: p.name, description: p.description || "", price: p.price, category: p.category || "Sarees", tag: p.tag || "", image_url: p.image_url || "", in_stock: !p.in_stock });
      loadProducts();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`"${p.name}" ko delete karna hai?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product delete ho gaya");
      if (editingId === p.id) { setEditingId(null); setForm(EMPTY_FORM); }
      loadProducts();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      const r = await api.put("/admin/settings", settings);
      setSettings(r.data);
      toast.success("Settings save ho gayi");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const login = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    const password = e.target.password.value;
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data);
      loadProducts();
      loadSettings();
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(formatApiError(err, "Login fail ho gaya"));
    }
  };

  // ---------- Loading session ----------
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ivory">
        <p className="font-display text-2xl text-maroon animate-pulse">Iralakshmi Admin...</p>
      </div>
    );
  }

  // ---------- Login ----------
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ivory px-4">
        <div className="w-full max-w-md rounded-3xl border border-gold-line bg-white p-8 shadow-[0_12px_40px_-8px_rgba(122,28,40,0.15)]">
          <div className="flex flex-col items-center gap-2 mb-8 text-center">
            <img src="/logo-mark.png" alt="Ira Lakshmi logo" className="h-16 w-16 rounded-full object-cover ring-2 ring-gold/60 shadow-md" />
            <h1 data-testid="admin-login-heading" className="font-display text-3xl font-bold text-maroon-deep">Admin Panel</h1>
            <p className="text-sm text-ink-muted">Ira Lakshmi — owner access only</p>
          </div>
          <form data-testid="admin-login-form" onSubmit={login} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input data-testid="admin-login-email-input" id="email" name="email" type="email" placeholder="admin@iralakshmi.fashion" required className="border-gold-line focus-visible:ring-maroon" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input data-testid="admin-login-password-input" id="password" name="password" type="password" placeholder="Your password" required className="border-gold-line focus-visible:ring-maroon" />
            </div>
            <Button data-testid="admin-login-submit" type="submit" className="w-full rounded-full bg-maroon hover:bg-maroon-dark text-white py-6 font-semibold">
              Log In
            </Button>
          </form>
          <Link data-testid="admin-back-to-shop" to="/" className="mt-6 block text-center text-sm text-ink-muted hover:text-maroon transition-colors duration-200">
            ← Back to shop
          </Link>
        </div>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div className="min-h-screen bg-ivory font-body">
      <header className="sticky top-0 z-40 bg-ivory/90 backdrop-blur-md border-b border-gold-line">
        <div className="mx-auto max-w-7xl flex items-center justify-between px-4 sm:px-8 h-16">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-maroon text-gold"><Store className="w-5 h-5" /></span>
            <div className="leading-tight">
              <p className="font-display text-xl font-bold text-maroon-deep">Admin Panel</p>
              <p className="text-[11px] text-ink-muted">Ira Lakshmi</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link data-testid="admin-view-shop-btn" to="/" className="inline-flex items-center gap-2 rounded-full border border-gold-line bg-white px-4 py-2 text-sm font-medium text-maroon hover:border-gold transition-colors duration-200">
              <ExternalLink className="w-4 h-4" />
              <span className="hidden sm:inline">View Shop</span>
            </Link>
            <Button data-testid="admin-logout-btn" onClick={logout} variant="outline" className="rounded-full border-gold-line text-ink-muted hover:text-maroon">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-8 py-8 sm:py-10 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 sm:gap-6">
          {[
            { id: "stats-products", icon: Package, label: "Total Products", value: stats.total },
            { id: "stats-categories", icon: Layers, label: "Categories", value: stats.categories },
            { id: "stats-instock", icon: Store, label: "In Stock", value: stats.inStock },
          ].map((s) => (
            <div key={s.id} data-testid={s.id} className="rounded-2xl border border-gold-line bg-white px-4 sm:px-6 py-4 sm:py-5">
              <s.icon className="w-5 h-5 text-gold-text mb-2" />
              <p className="font-display text-2xl sm:text-3xl font-bold text-maroon-deep">{s.value}</p>
              <p className="text-[11px] sm:text-xs text-ink-muted">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-5 gap-6 items-start">
          <div className="lg:col-span-2 space-y-6">
          {/* Product form */}
          <InstagramImport onAdd={(urls) => setForm((s) => ({ ...s, images: [...(s.images || []), ...urls] }))} />

          <form data-testid="product-form" onSubmit={saveProduct} className="space-y-4 rounded-3xl border border-gold-line bg-white p-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h2 data-testid="product-form-heading" className="font-display text-2xl font-semibold text-maroon-dark">
                {editingId ? "Edit Product" : "Add New Product"}
              </h2>
              {editingId ? (
                <button type="button" data-testid="product-form-cancel-btn" onClick={() => { setEditingId(null); setForm(EMPTY_FORM); }} className="text-xs font-medium text-ink-muted hover:text-maroon">
                  Cancel
                </button>
              ) : null}
            </div>

            <div>
              <Label className="mb-2 block">Product Photos (first photo = cover)</Label>
              <label
                htmlFor="image-upload"
                data-testid="product-image-dropzone"
                className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gold-line bg-ivory/70 py-6 cursor-pointer hover:border-gold transition-colors duration-200 ${uploading ? "opacity-60 pointer-events-none" : ""}`}
              >
                {uploading ? <Upload className="w-7 h-7 text-maroon animate-bounce" /> : <ImagePlus className="w-7 h-7 text-gold-text" />}
                <span className="text-sm text-ink-muted">{uploading ? "Uploading..." : "Tap to choose photos"}</span>
                <span className="text-[11px] text-ink-muted/70">Add 3-4 photos like Instagram • JPG / PNG / WebP</span>
              </label>
              <input data-testid="product-image-input" id="image-upload" type="file" accept="image/*" multiple className="hidden" onChange={onFile} />

              {form.images && form.images.length > 0 ? (
                <div data-testid="image-manager" className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {form.images.map((img, i) => (
                    <div key={img + i} data-testid={`image-thumb-${i}`} className="group relative overflow-hidden rounded-lg border border-gold-line">
                      <img src={fileUrl(img)} alt={`Photo ${i + 1}`} className="aspect-[3/4] w-full object-cover" />
                      {i === 0 ? (
                        <span className="absolute top-1 left-1 rounded bg-maroon px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-gold-soft">Cover</span>
                      ) : null}
                      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-ink/60 px-1 py-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                        {i !== 0 ? (
                          <button type="button" data-testid={`make-cover-btn-${i}`} onClick={() => makeCover(i)} className="text-[9px] font-bold uppercase tracking-wide text-white hover:text-gold-soft">Cover</button>
                        ) : <span />}
                        <button type="button" data-testid={`remove-image-btn-${i}`} onClick={() => removeImage(i)} title="Remove photo" className="text-red-300 hover:text-red-500">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="p-name">Product Name</Label>
              <Input data-testid="product-name-input" id="p-name" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="Kanjivaram Silk Saree" required className="border-gold-line" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="p-desc">Description</Label>
              <Textarea data-testid="product-description-input" id="p-desc" value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} placeholder="Fabric, design, blouse piece — details customers ask about" rows={3} className="border-gold-line" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="p-price">Sale Price (₹)</Label>
                <Input data-testid="product-price-input" id="p-price" type="number" min="0" value={form.price} onChange={(e) => setForm((s) => ({ ...s, price: e.target.value }))} placeholder="2499" required className="border-gold-line" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-mrp">Original Price / MRP (₹)</Label>
                <Input data-testid="product-mrp-input" id="p-mrp" type="number" min="0" value={form.mrp || ""} onChange={(e) => setForm((s) => ({ ...s, mrp: e.target.value }))} placeholder="Optional — shows a sale badge" className="border-gold-line" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="p-cat">Category</Label>
                <Input data-testid="product-category-input" id="p-cat" list="category-list" value={form.category} onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))} placeholder="Sarees" className="border-gold-line" />
                <datalist id="category-list">
                  {CATEGORIES.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-tag">Tag (optional)</Label>
                <Input data-testid="product-tag-input" id="p-tag" value={form.tag} onChange={(e) => setForm((s) => ({ ...s, tag: e.target.value }))} placeholder="Best Seller / New Arrival" className="border-gold-line" />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
              <input data-testid="product-instock-toggle" type="checkbox" checked={form.in_stock} onChange={(e) => setForm((s) => ({ ...s, in_stock: e.target.checked }))} className="h-4 w-4 accent-maroon" />
              In stock
            </label>

            <Button data-testid="product-save-btn" type="submit" disabled={saving || uploading} className="w-full rounded-full bg-maroon hover:bg-maroon-dark text-white py-6 font-semibold disabled:opacity-50">
              {saving ? "Publishing..." : editingId ? "Update Product" : "Publish"}
            </Button>
          </form>
          </div>

          {/* Product list */}
          <div className="lg:col-span-3 space-y-6">
            <div data-testid="product-list" className="rounded-3xl border border-gold-line bg-white p-5 sm:p-6">
              <div className="flex items-center justify-between gap-3 mb-5">
                <h2 className="font-display text-2xl font-semibold text-maroon-dark">Your Products</h2>
                <Button type="button" data-testid="admin-export-csv-btn" onClick={exportTaps} variant="outline" className="rounded-full border-gold-line text-maroon hover:border-gold hover:bg-gold-soft/40 text-xs sm:text-sm shrink-0">
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Download Report</span>
                  <span className="sm:hidden">Report</span>
                </Button>
              </div>
              {products.length === 0 ? (
                <p className="py-10 text-center text-sm text-ink-muted">No products yet — add your first product using the form.</p>
              ) : (
                <ul className="divide-y divide-gold-line/70">
                  {products.map((p) => (
                    <li key={p.id} data-testid={`admin-product-row-${p.id}`} className="flex flex-wrap items-center gap-3 sm:gap-4 py-3">
                      <img data-testid={`admin-product-thumb-${p.id}`} src={fileUrl(p.image_url)} alt={p.name} className="h-14 w-12 rounded-lg object-cover border border-gold-line" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-ink">{p.name}</p>
                        <p className="text-xs text-ink-muted">
                          {p.category} • <span className="font-semibold text-maroon">{inr(p.price)}</span>
                          {p.mrp && Number(p.mrp) > Number(p.price) ? <span className="line-through ml-1.5">{inr(p.mrp)}</span> : null}
                          <span data-testid={`admin-product-taps-${p.id}`} className="ml-1.5 text-gold-text font-semibold">• {p.whatsapp_taps || 0} WhatsApp taps</span>
                        </p>
                      </div>
                      <button data-testid={`admin-product-stock-btn-${p.id}`} onClick={() => toggleStock(p)} title={p.in_stock ? "Stock mein hai" : "Out of stock"} className="p-2 rounded-full text-ink-muted hover:text-maroon hover:bg-gold-soft/60 transition-colors duration-200">
                        {p.in_stock ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button data-testid={`admin-product-edit-btn-${p.id}`} onClick={() => editProduct(p)} title="Edit" className="p-2 rounded-full text-ink-muted hover:text-maroon hover:bg-gold-soft/60 transition-colors duration-200">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button data-testid={`admin-product-delete-btn-${p.id}`} onClick={() => deleteProduct(p)} title="Delete" className="p-2 rounded-full text-ink-muted hover:text-red-600 hover:bg-red-50 transition-colors duration-200">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Settings */}
            <form data-testid="settings-form" onSubmit={saveSettings} className="rounded-3xl border border-gold-line bg-white p-5 sm:p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Settings2 className="w-5 h-5 text-gold-text" />
                <h2 className="font-display text-2xl font-semibold text-maroon-dark">Shop Settings</h2>
              </div>
              <div className="space-y-2">
                <Label htmlFor="s-wa">WhatsApp Number (customer orders arrive here)</Label>
                <Input data-testid="settings-whatsapp-input" id="s-wa" value={settings.whatsapp_number || ""} onChange={(e) => setSettings((s) => ({ ...s, whatsapp_number: e.target.value }))} placeholder="918800585027" className="border-gold-line" />
                <p className="text-xs text-ink-muted">Include the country code — e.g. 918800585027.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="s-ig">Instagram Page URL</Label>
                <Input data-testid="settings-instagram-input" id="s-ig" value={settings.instagram_url || ""} onChange={(e) => setSettings((s) => ({ ...s, instagram_url: e.target.value }))} placeholder="https://www.instagram.com/iralakshmi.fashion/" className="border-gold-line" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="s-offer">Offer Banner (optional)</Label>
                <Input data-testid="settings-offer-input" id="s-offer" value={settings.offer_text || ""} onChange={(e) => setSettings((s) => ({ ...s, offer_text: e.target.value }))} placeholder="Festive Offer: Free delivery on orders above Rs 2,999" className="border-gold-line" />
              </div>
              <Button data-testid="settings-save-btn" type="submit" className="rounded-full bg-maroon hover:bg-maroon-dark text-white px-8 font-semibold">
                Save Settings
              </Button>
            </form>

            <AdminAccounts currentUserId={user?.id} />

            <AdminFeedback />
          </div>
        </div>
      </main>
    </div>
  );
}
